import React from 'react';
import i18n from 'util/i18n';
import { formConfig } from 'config/forms';
import { getNumberOfYearsString } from 'helpers/text';

import { Summary, DateSummary } from 'components/Summary';
import { SUBSTANCE_USE, SUBSTANCE_USE_ALCOHOL_ORDERED } from 'config/formSections/substanceUse';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { EmptyListInstance } from 'forms/shared/models';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { alcoholMandatoryCounselingAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import OrderedCounselingItem from './OrderedCounselingItem';

const sectionConfig = {
  key: SUBSTANCE_USE_ALCOHOL_ORDERED.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_ALCOHOL_ORDERED.name,
  storeKey: SUBSTANCE_USE_ALCOHOL_ORDERED.storeKey,
};

export class OrderedCounselings extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.summary = this.summary.bind(this);
    this.updateHasBeenOrdered = this.updateHasBeenOrdered.bind(this);
    this.updateList = this.updateList.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      HasBeenOrdered: this.props.HasBeenOrdered,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateHasBeenOrdered = (values) => {
    this.update({
      HasBeenOrdered: values,
      List: values.value === 'Yes' ? this.props.List : [],
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const counselingDates = DateSummary(o.CounselingDates);
    const counselingTypes = {
      Employer: 'Employer',
      MedicalProfessional: 'Medical professional',
      MentalHealthProfessional: 'Mental health professional',
      CourtOfficial: 'Court official',
      NotOrdered: 'Not ordered',
    };

    const seekers = [];
    if (o.Seekers && o.Seekers.values) {
      o.Seekers.values.forEach((seeker) => {
        if (seeker === 'Other') {
          seekers.push((o.OtherSeeker || {}).value || 'Other');
        } else {
          seekers.push(counselingTypes[seeker]);
        }
      });
    }

    return Summary({
      type: i18n.t('substance.alcohol.orderedCounseling.collection.itemType'),
      index,
      left: seekers.join(', '),
      right: counselingDates,
      placeholder: i18n.t('substance.alcohol.receivedCounseling.collection.summary'),
    });
  };

  render() {
    const {
      formType, errors, requireAlcoholOrderedCounselingParty, peopleWhoKnowYou, List
    } = this.props;
    const formTypeConfig = formType && formConfig[formType];
    const years = formTypeConfig && formTypeConfig.SUBSTANCE_ALCOHOL_TREATMENT_YEARS;
    const isPVQ = isPVQForm(this.props.formType);

    let branchLabelCopy;
    if (years === 'EVER') {
      branchLabelCopy = i18n.t('substance.alcohol.heading.orderedCounseling');
    } else {
      const numberOfYearsString = getNumberOfYearsString(years);
      branchLabelCopy = i18n.t('substance.alcohol.heading.orderedCounselingNum', {
        numberOfYearsString,
      });
    }

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, alcoholMandatoryCounselingAddressNameFields);
    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return isPVQ ? (
      <div
        className="section-content ordered-counselings"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_ALCOHOL_ORDERED.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.alcohol.ordered')}</h1>
        <Branch
          {...this.props.HasBeenOrdered}
          name="HasBeenOrdered"
          attribute="HasBeenOrdered"
          label={i18n.t('substance.pvq.alcohol.heading.orderedCounseling')}
          labelSize="h4"
          className="has-been-ordered"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateHasBeenOrdered} // eslint-disable-line @typescript-eslint/unbound-method
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.HasBeenOrdered.value === 'Yes'
          && (
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
            onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.pvq.alcohol.orderedCounseling.collection.description')}
            appendTitle={i18n.t('substance.pvq.alcohol.orderedCounseling.collection.appendTitle')}
            appendLabel={i18n.t('substance.pvq.alcohol.orderedCounseling.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
          >
            <OrderedCounselingItem
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
            />
          </Accordion>
          )}
      </div>
    ) : (
      <div
        className="section-content ordered-counselings"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_ALCOHOL_ORDERED.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.alcohol.ordered')}</h1>
        <Branch
          name="HasBeenOrdered"
          attribute="HasBeenOrdered"
          label={branchLabelCopy}
          labelSize="h4"
          className="has-been-ordered"
          {...this.props.HasBeenOrdered}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateHasBeenOrdered} // eslint-disable-line @typescript-eslint/unbound-method
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
        <Show when={this.props.HasBeenOrdered.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
            onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.alcohol.orderedCounseling.collection.description')}
            appendTitle={i18n.t('substance.alcohol.orderedCounseling.collection.appendTitle')}
            appendLabel={i18n.t('substance.alcohol.orderedCounseling.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <OrderedCounselingItem
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireAlcoholOrderedCounselingParty={requireAlcoholOrderedCounselingParty}
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.comments.summary')}
            name="SummaryComments"
            className="summary-comment"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

OrderedCounselings.defaultProps = {
  HasBeenOrdered: {},
  List: EmptyListInstance,
  Comments: {},
  SummaryComments: {},
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'alcohol/ordered',
  addressBooks: {},
  dispatch: () => { },
  scrollToBottom: '',
  requireAlcoholOrderedCounselingParty: true,
  errors: [],
};

export default connectSubsection(OrderedCounselings, sectionConfig);
